import type { ReapproNeedMethod } from '@gozoki/api-types'
import { dayDiffIso, formatDecimalMax } from '@gozoki/tools'
import type { PermissionTuple } from '~/utils/ability'

export const FORECAST_PILOT_READ: PermissionTuple = ['read', 'categories.reappro.forecast-pilot']
export const FORECAST_PILOT_EDIT: PermissionTuple = ['edit', 'categories.reappro.forecast-pilot']

export type PilotScopeStatus = 'pending' | 'computing' | 'send_imminent' | 'sent'

const parisTime = (iso: string) =>
  new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))

export const parisDayTime = (iso: string) =>
  new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(new Date(iso))
    .replace(',', '')

export const scopeStatusLabel = (status: PilotScopeStatus, sendAt: string) => {
  if (status === 'sent') return 'Envoyé'
  if (status === 'computing') return 'Calcul en cours : plus de recalcul'
  if (status === 'send_imminent') return `Envoi imminent (${parisTime(sendAt)}) : plus de recalcul`
  return `Fenêtre de modification ouverte jusqu’à ${parisTime(sendAt)}`
}

export const inProgressDeliveries = <T extends { status: PilotScopeStatus }>(rows: T[]) =>
  rows.filter((row) => row.status !== 'sent')

export const splitPreviewScopes = <T extends { status: PilotScopeStatus; deliveryDate: string }>(
  scopes: T[],
) => {
  const byDate = [...scopes].sort((a, b) => a.deliveryDate.localeCompare(b.deliveryDate))
  return {
    editable: byDate.filter((scope) => scope.status === 'pending'),
    frozen: byDate.filter((scope) => scope.status !== 'pending'),
  }
}

const FROZEN_STATUS_LABELS: Record<Exclude<PilotScopeStatus, 'pending'>, string> = {
  sent: 'déjà envoyée(s)',
  computing: 'en cours de calcul',
  send_imminent: 'à l’envoi imminent',
}

export const frozenScopesLabel = (frozen: { status: PilotScopeStatus }[]) => {
  const parts = (['sent', 'computing', 'send_imminent'] as const).flatMap((status) => {
    const count = frozen.filter((scope) => scope.status === status).length
    return count === 0 ? [] : [`${count} ${FROZEN_STATUS_LABELS[status]}`]
  })
  return `${frozen.length} livraison(s) non modifiable(s) (${parts.join(', ')}) : inchangée(s).`
}

export const anchorLabel = (anchorAt: string | null) =>
  anchorAt === null ? 'Aucune sur 7 jours' : parisDayTime(anchorAt)

export const deliveryDateLabel = (isoDate: string) => {
  const [year, month, day] = isoDate.split('-')
  return `${day}/${month}/${year}`
}

export const nextComputeLabel = (next: {
  kind: 'production' | 'dispatch'
  computeAt: string
  deliveryDate: string
}) =>
  `${next.kind === 'dispatch' ? 'dispatch' : 'calcul'} le ${parisDayTime(next.computeAt)} · livraison du ${deliveryDateLabel(next.deliveryDate)}`

/** Écart entre l'ouverture et aujourd'hui, en jours calendaires : « dans 8 j », « aujourd'hui », « J+3 ». */
export const openingCountdownLabel = (openingDate: string, todayIso: string) => {
  const days = dayDiffIso(todayIso, openingDate)
  if (days === 0) return 'aujourd’hui'
  return days > 0 ? `dans ${days} j` : `J+${-days}`
}

export const methodLabel = (method: ReapproNeedMethod) =>
  method === 'forecast' ? 'Prévision' : 'Traditionnel'

export const filterCandidatesByLabel = <T extends { label: string }>(
  candidates: T[],
  search: string,
): T[] => {
  const normalized = search.trim().toLowerCase()
  if (normalized === '') return candidates
  return candidates.filter((candidate) => candidate.label.toLowerCase().includes(normalized))
}

export const hasPilotSelectionChanged = (initialIds: number[], selectedIds: Set<number>) => {
  if (initialIds.length !== selectedIds.size) return true
  return initialIds.some((id) => !selectedIds.has(id))
}

/** Nombre d'articles ou de mètres, à une décimale au plus. */
export const formatQuantity = (value: number): string => formatDecimalMax(value, 1)
